import { useRef, useMemo } from 'react';
import shallowEqual from 'utils/shallowEqual';

/**
 * Custom hook that memoizes a selector result with shallow equality checking.
 * Prevents unnecessary re-renders when the selected data hasn't actually changed.
 *
 * @param value - The value to apply the selector to
 * @param selector - Optional function to select/derive data from the value
 * @returns The memoized result of the selector (or the value itself if no selector)
 */
export const useMemoizedSelector = <TValue, TSelected = TValue>(
  value: TValue,
  selector?: (value: TValue) => TSelected,
): TValue | TSelected => {
  const prevValueRef = useRef<TValue>(value);
  const prevResultRef = useRef<TSelected>(undefined);

  return useMemo(() => {
    if (selector) {
      const result = selector(value);

      // If value reference changed, check if selected slice actually changed
      if (prevValueRef.current !== value) {
        const prevResult = prevResultRef.current;

        // Shallow compare the results
        if (prevResult && shallowEqual(result, prevResult)) {
          // Data hasn't changed, return previous reference to prevent re-render
          return prevResult;
        }

        // Data changed or first run, update refs and return new result
        prevValueRef.current = value;
        prevResultRef.current = result;
        return result;
      }

      // Value hasn't changed, return cached result
      return prevResultRef.current ?? result;
    }

    return value;
  }, [value]); // Only state, intentionally ignoring selector changes
};

export default useMemoizedSelector;
